import React, {
  MouseEvent,
} from 'react';
import {
  Row,
} from '@tanstack/react-table';
import clsx from 'clsx';
import IconButton from '@mui/material/IconButton';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  TemplateFieldDTO,
  TemplateListItemDTO,
  UserViewDTO,
} from 'dtos';
import {
  useUserProfilePicturePath,
} from 'pages/Dashboard/hooks/media';
import Name from 'pages/Dashboard/components/Name';
import ToolTip from 'core/components/Tooltip';
import {
  useConfirmDialog,
  useNotify,
} from 'core/hooks';
import {
  DeleteIcon,
} from 'core/icons';
import {
  useDeleteTemplate,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  getSectionLabelByName,
} from 'views/EMR/Settings/helper';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  useDeleteField,
} from 'views/EMR/Settings/hooks';

type TemplateActionsCellProps = {
  row: Row<TemplateListItemDTO>;
};

function TemplateActionsCell({ row }: TemplateActionsCellProps) {
  const { confirm, closeConfirm } = useConfirmDialog();
  const userInfo = useUserInfo();
  const notify = useNotify();
  const disabled = row.original.userId !== userInfo?.user?.userId;
  const { mutateAsync: deleteTemplate } = useDeleteTemplate();

  return (
    <ToolTip title={disabled
      ? (
        <>
          You are not allowed to remove templates
          <br />
          that are created by another user.
        </>
      ) : 'Delete'}
    >
      <div className={clsx({ 'cursor-not-allowed opacity-50': disabled })}>
        <IconButton
          color="error"
          size="small"
          disabled={disabled}
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            confirm({
              title: 'Template Delete',
              content: `Are you sure you want to delete "${row.original?.templateName}"?`,
              onConfirm: async () => {
                await deleteTemplate({
                  templateName: row.original?.templateName,
                  sectionName: row.original?.sectionName,
                });
                closeConfirm();
                notify({
                  message: 'Template Deleted Successfully!',
                  variant: 'success',
                });
                settingsEventChannel.emit('onVisitNoteSettingsChange');
              },
            });
          }}
        >
          <DeleteIcon className="!w-5 !h-5 !text-gray-500" />
        </IconButton>
      </div>
    </ToolTip>
  );
}

type FieldActionsCellProps = {
  row: {
    original: TemplateFieldDTO;
  };
};

function FieldActionsCell({ row }: FieldActionsCellProps) {
  const { confirm, closeConfirm } = useConfirmDialog();
  const userInfo = useUserInfo();
  const notify = useNotify();
  const disabled = row.original?.userId !== userInfo?.user?.userId;
  const { mutateAsync: deleteTemplate } = useDeleteField();

  return (
    <ToolTip title={disabled
      ? (
        <>
          You are not allowed to remove templates
          <br />
          that are created by another user.
        </>
      ) : 'Delete'}
    >
      <div className={clsx({ 'cursor-not-allowed opacity-50': disabled })}>
        <IconButton
          color="error"
          size="small"
          disabled={disabled}
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            confirm({
              title: 'Template Delete',
              content: `Are you sure you want to delete "${row.original?.title}"?`,
              onConfirm: async () => {
                await deleteTemplate(row.original);
                closeConfirm();
                notify({
                  message: 'Field Deleted Successfully!',
                  variant: 'success',
                });
                settingsEventChannel.emit('onVisitNoteSettingsChange');
              },
            });
          }}
        >
          <DeleteIcon className="!w-5 !h-5 !text-gray-500" />
        </IconButton>
      </div>
    </ToolTip>
  );
}

type CreatedByCellProps = {
  user?: UserViewDTO;
};

function CreatedByCell({ user }: CreatedByCellProps) {
  const src = useUserProfilePicturePath(user?.userId);

  return (
    <Name
      image={{ src }}
      {...user?.formattedNames}
    />
  );
}

export const baseTemplateColumns: ColumnDef<TemplateListItemDTO>[] = [{
  accessorKey: 'templateName',
  size: 240,
  columnTitle: 'Name',
}, {
  accessorKey: 'userId',
  meta: { autoSized: true },
  columnTitle: 'Created by',
  cell: ({ row: { original: { user } } }) => <CreatedByCell user={user} />,
}, {
  id: 'actions',
  size: 48,
  header: '',
  cell: ({ row }) => <TemplateActionsCell row={row} />,
}];

export const sectionTemplateColumns: ColumnDef<TemplateListItemDTO>[] = [
  baseTemplateColumns[0],
  {
    accessorKey: 'sectionName',
    size: 240,
    columnTitle: 'Section',
    cell: ({ row }) => getSectionLabelByName(row.original?.sectionName ?? '--'),
  },
  ...baseTemplateColumns.slice(1),
];

export const fieldColumns: ColumnDef<TemplateFieldDTO>[] = [{
  accessorKey: 'title',
  size: 240,
  columnTitle: 'Title',
}, {
  accessorKey: 'userId',
  meta: { autoSized: true },
  columnTitle: 'Created by',
  cell: ({ row: { original: { user } } }) => <CreatedByCell user={user} />,
}, {
  id: 'actions',
  size: 48,
  header: '',
  cell: ({ row }) => <FieldActionsCell row={row} />,
}];
